import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { History, RefreshCcw } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Panel reutilizable que muestra el historial de cambios de un documento.
 * Props:
 *   - coleccion: "clientes" | "cotizaciones" | "contratos" | "eventos" | "pagos" | ...
 *   - docId: id del documento
 *   - titulo (opcional)
 */
const HistorialPanel = ({ coleccion, docId, titulo = "Historial de cambios" }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHist = useCallback(async () => {
    if (!coleccion || !docId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/historial/${coleccion}/${docId}`);
      setEntries(res.data.historial || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [coleccion, docId]);

  useEffect(() => { fetchHist(); }, [fetchHist]);

  const colorByAccion = (a) => {
    if (a === "creacion") return "bg-emerald-100 text-emerald-800";
    if (a === "edicion") return "bg-blue-100 text-blue-800";
    if (a === "cancelacion") return "bg-red-100 text-red-700";
    if (a === "registrar_abono") return "bg-amber-100 text-amber-800";
    if (a === "validacion") return "bg-emerald-100 text-emerald-800";
    if (a === "firma") return "bg-purple-100 text-purple-800";
    return "bg-stone-100 text-stone-700";
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm" data-testid={`historial-${coleccion}-${docId}`}>
      <div className="flex items-center justify-between p-3 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-stone-500" />
          <span className="font-medium text-stone-800 text-sm">{titulo}</span>
          <span className="text-xs text-stone-500">({entries.length})</span>
        </div>
        <Button size="sm" variant="outline" onClick={fetchHist} className="h-7 px-2 border-stone-300" data-testid={`btn-refresh-historial-${docId}`}>
          <RefreshCcw className="w-3 h-3" />
        </Button>
      </div>
      <div className="p-3 max-h-72 overflow-y-auto space-y-3">
        {loading ? (
          <p className="text-xs text-stone-400">Cargando...</p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-stone-400 italic">Sin movimientos registrados.</p>
        ) : (
          entries.map((e, i) => (
            <div key={e.fecha + i} className="border-l-2 border-stone-200 pl-3 text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${colorByAccion(e.accion)}`}>
                  {e.accion}
                </span>
                <span className="text-stone-500">{new Date(e.fecha).toLocaleString("es-MX")}</span>
              </div>
              <p className="text-stone-700 mt-1">{e.descripcion}</p>
              <p className="text-stone-400">por {e.usuario_nombre || e.usuario_email}</p>
              {e.cambios && Object.keys(e.cambios).length > 0 && (
                <div className="mt-1 space-y-0.5 text-[11px]">
                  {Object.entries(e.cambios).slice(0, 8).map(([campo, val]) => (
                    <div key={campo} className="text-stone-500">
                      <span className="font-medium text-stone-600">{campo}:</span>{" "}
                      <span className="line-through text-red-500">{val?.antes ? String(val.antes).substring(0, 40) : "—"}</span>
                      {" → "}
                      <span className="text-emerald-700">{val?.despues ? String(val.despues).substring(0, 40) : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HistorialPanel;
